import {
  addDays,
  parseDateKey,
  startOfWeek,
} from "@/modules/calendar/calendarUtils";
import type { WorkCategory, WorkItem } from "@/modules/work/workTypes";

/** Default weeks shown; the range starts one week before the anchor week. */
export const TIMELINE_WEEKS = 6;

/** Zoom presets (px per day) and the free ⌘/Ctrl + wheel limits. */
export const TIMELINE_ZOOM_PRESETS = {
  days: 112,
  weeks: 40,
  months: 16,
} as const;
export type TimelineZoomPreset = keyof typeof TIMELINE_ZOOM_PRESETS;
export const TIMELINE_MIN_DAY_WIDTH = 12;
export const TIMELINE_MAX_DAY_WIDTH = 160;

/** Multi-day tasks and project spans become cards from this width up. */
export const TIMELINE_CARD_MIN_PX = 168;

/** Single-day items show their title inside the day from this width up. */
export const TIMELINE_INSIDE_DAY_PX = 96;

export const clampTimelineDayWidth = (width: number) =>
  Math.round(
    Math.min(TIMELINE_MAX_DAY_WIDTH, Math.max(TIMELINE_MIN_DAY_WIDTH, width)),
  );

/** Preset closest to a free zoom width (for the Days / Weeks / Months switch). */
export const getTimelineZoomPreset = (dayWidth: number): TimelineZoomPreset =>
  (Object.entries(TIMELINE_ZOOM_PRESETS) as [TimelineZoomPreset, number][])
    .map(
      ([preset, width]) =>
        [preset, Math.abs(Math.log(dayWidth / width))] as const,
    )
    .sort((left, right) => left[1] - right[1])[0][0];

/** Enough weeks to fill a wide screen at this zoom (6 – 52). */
export const getTimelineWeekCount = (dayWidth: number) =>
  Math.min(52, Math.max(TIMELINE_WEEKS, Math.ceil(1800 / (dayWidth * 7))));

/** Rows for items without a project, in this order (after Projects). */
const CATEGORY_ROWS: { category: WorkCategory; label: string }[] = [
  { category: "task", label: "Tasks" },
  { category: "meeting", label: "Meetings" },
  { category: "follow_up", label: "Follow-ups" },
  { category: "activity", label: "Activities" },
  { category: "delivery", label: "Deliveries" },
];

export type TimelineRange = {
  start: Date;
  days: Date[];
};

export type TimelinePlacedItem = {
  item: WorkItem;
  /** First visible column (start day, clamped to the range). */
  dayIndex: number;
  /** Columns the bar covers: 1 for single-day items. */
  barDays: number;
  /** The label fits inside the bar; otherwise it is drawn beside it. */
  labelInside: boolean;
  /** Bar continues before / after the visible range. */
  clippedStart: boolean;
  clippedEnd: boolean;
  /** Columns reserved in the lane (bar + outside label). */
  span: number;
  /** Wide multi-day task: drawn as a card in the row's card lanes. */
  card: boolean;
  /** Lane when titles are shown (expanded row). */
  lane: number;
  /** Lane in a compact row: markers only, so only same-day items stack. */
  compactLane: number;
};

/** A project's planned span: start_date → delivery (expected end). */
export type TimelineProjectSpan = {
  dealId: NonNullable<WorkItem["dealId"]>;
  name: string;
  startDate: string | null;
  endDate: string | null;
  /** Card details (client, description, team). */
  companyId?: number | string | null;
  companyName?: string | null;
  description?: string | null;
  memberIds?: (number | string)[];
};

/** Project summary bar drawn at the top of its row. */
export type TimelineSummary = {
  dayIndex: number;
  barDays: number;
  clippedStart: boolean;
  clippedEnd: boolean;
  startDate: string | null;
  endDate: string | null;
};

export type TimelineGroup = {
  key: string;
  label: string;
  /** "project": one row per project; "category": items without a project. */
  kind: "project" | "category";
  category?: WorkCategory;
  dealId: WorkItem["dealId"];
  /** Projects only: start → delivery bar (replaces their start/delivery markers). */
  summary?: TimelineSummary;
  /** The project the summary bar belongs to (card details). */
  project?: TimelineProjectSpan;
  items: TimelinePlacedItem[];
  /** Card lanes (wide multi-day tasks), drawn above the regular lanes. */
  cardLaneCount: number;
  /** Lanes used by items (0 when a project row only has its summary bar). */
  laneCount: number;
  /** Lanes needed when the row is compact (markers only, no outside titles). */
  compactLaneCount: number;
};

export type TimelineSegment = {
  key: string;
  label: string;
  startIndex: number;
  length: number;
};

export const getTimelineRange = (
  anchor: Date,
  weeks = TIMELINE_WEEKS,
  /** Weeks before the anchor week (scroll buffer for the infinite timeline). */
  leadWeeks = 1,
): TimelineRange => {
  const start = addDays(startOfWeek(anchor), -7 * leadWeeks);
  return {
    start,
    days: Array.from({ length: weeks * 7 }, (_, index) =>
      addDays(start, index),
    ),
  };
};

export const getTimelineDayIndex = (start: Date, dateKey: string) =>
  Math.round(
    (parseDateKey(dateKey).getTime() - start.getTime()) / (24 * 60 * 60 * 1000),
  );

export const formatTimelineRangeLabel = (range: TimelineRange) => {
  const first = range.days[0];
  const last = range.days[range.days.length - 1];
  const format = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `${format(first)} – ${format(last)}, ${last.getFullYear()}`;
};

export const getTimelineMonthSegments = (days: Date[]): TimelineSegment[] => {
  const segments: TimelineSegment[] = [];
  days.forEach((day, index) => {
    const key = `${day.getFullYear()}-${day.getMonth()}`;
    const current = segments[segments.length - 1];
    if (current?.key === key) {
      current.length += 1;
      return;
    }
    segments.push({
      key,
      label: day.toLocaleDateString("en-US", {
        month: "long",
        ...(day.getMonth() === 0 ? { year: "numeric" } : {}),
      }),
      startIndex: index,
      length: 1,
    });
  });
  return segments;
};

/** Rough width of an item's text label, in columns. */
const getLabelDays = (title: string, dayWidth: number, withAvatar: boolean) =>
  Math.ceil(
    (Math.min(title.length, 34) * 6.5 + 24 + (withAvatar ? 24 : 0)) / dayWidth,
  );

/** Width needed to draw the label inside a bar. */
const INSIDE_LABEL_MIN_PX = 120;

/**
 * Where an item sits on the range: single-day items are a marker on the due
 * day, multi-day tasks a bar from start_date to the due day. Null when the
 * item is entirely outside the range.
 */
export const placeTimelineItem = (
  item: WorkItem,
  range: TimelineRange,
  dayWidth: number,
): Omit<TimelinePlacedItem, "lane" | "compactLane"> | null => {
  const dueIndex = getTimelineDayIndex(range.start, item.date);
  const startIndex =
    item.startDate && item.startDate < item.date
      ? getTimelineDayIndex(range.start, item.startDate)
      : dueIndex;
  const lastIndex = range.days.length - 1;
  if (dueIndex < 0 || startIndex > lastIndex) return null;

  const dayIndex = Math.max(0, startIndex);
  const endIndex = Math.min(lastIndex, dueIndex);
  const barDays = endIndex - dayIndex + 1;
  const labelInside =
    barDays > 1
      ? barDays * dayWidth >= INSIDE_LABEL_MIN_PX
      : dayWidth >= TIMELINE_INSIDE_DAY_PX;
  const span = Math.min(
    lastIndex + 1 - dayIndex,
    labelInside
      ? barDays
      : barDays +
          Math.min(
            11,
            getLabelDays(item.title, dayWidth, item.event.kind === "task"),
          ),
  );

  return {
    item,
    dayIndex,
    barDays,
    labelInside,
    clippedStart: startIndex < 0,
    clippedEnd: dueIndex > lastIndex,
    span: Math.max(span, barDays),
    card:
      item.event.kind === "task" &&
      startIndex < dueIndex &&
      barDays * dayWidth >= TIMELINE_CARD_MIN_PX,
  };
};

export const placeTimelineProjectSpan = (
  span: TimelineProjectSpan,
  range: TimelineRange,
): TimelineSummary | null => {
  const start = span.startDate?.slice(0, 10) || span.endDate?.slice(0, 10);
  const end = span.endDate?.slice(0, 10) || span.startDate?.slice(0, 10);
  if (!start || !end) return null;
  const [first, last] = start <= end ? [start, end] : [end, start];
  const startIndex = getTimelineDayIndex(range.start, first);
  const endIndex = getTimelineDayIndex(range.start, last);
  const lastIndex = range.days.length - 1;
  if (endIndex < 0 || startIndex > lastIndex) return null;
  const dayIndex = Math.max(0, startIndex);
  return {
    dayIndex,
    barDays: Math.min(lastIndex, endIndex) - dayIndex + 1,
    clippedStart: startIndex < 0,
    clippedEnd: endIndex > lastIndex,
    startDate: span.startDate?.slice(0, 10) || null,
    endDate: span.endDate?.slice(0, 10) || null,
  };
};

/** Greedy lane packing: each item takes the first lane that is free. */
const packTimelineLanes = <T extends { dayIndex: number }>(
  items: T[],
  widthOf: (item: T) => number,
) => {
  const laneEnds: number[] = [];
  const lanes = items.map((item) => {
    let lane = laneEnds.findIndex((end) => end <= item.dayIndex);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = item.dayIndex + widthOf(item);
    return lane;
  });
  return { lanes, count: laneEnds.length };
};

const isProjectMilestone = (item: WorkItem) =>
  item.event.kind === "project_start" || item.event.kind === "project_delivery";

const sortByDateTime = (left: WorkItem, right: WorkItem) => {
  const leftTime = ("time" in left.event && left.event.time) || "";
  const rightTime = ("time" in right.event && right.event.time) || "";
  return `${left.date} ${leftTime}`.localeCompare(`${right.date} ${rightTime}`);
};

/**
 * Project rows (alphabetical) first, then one row per category for items
 * without a project (Tasks, Meetings, …). Items are packed into lanes.
 */
export const buildTimelineGroups = (
  items: WorkItem[],
  range: TimelineRange,
  dayWidth: number,
  projects: TimelineProjectSpan[] = [],
): TimelineGroup[] => {
  const byGroup = new Map<string, { group: TimelineGroup; raw: WorkItem[] }>();

  // Projects active in the range get a row even with no items in it.
  for (const project of projects) {
    const summary = placeTimelineProjectSpan(project, range);
    if (!summary) continue;
    const key = `project:${project.dealId}`;
    byGroup.set(key, {
      group: {
        key,
        kind: "project",
        label: project.name.trim() || `Project #${project.dealId}`,
        dealId: project.dealId,
        summary,
        project,
        items: [],
        cardLaneCount: 0,
        laneCount: 0,
        compactLaneCount: 0,
      },
      raw: [],
    });
  }

  for (const item of items) {
    if (!placeTimelineItem(item, range, dayWidth)) continue;
    // The summary bar already shows the project's start and delivery.
    if (
      isProjectMilestone(item) &&
      byGroup.get(`project:${item.dealId}`)?.group.summary
    ) {
      continue;
    }
    const isProject = item.dealId != null;
    const key = isProject
      ? `project:${item.dealId}`
      : `category:${item.category}`;
    let entry = byGroup.get(key);
    if (!entry) {
      entry = {
        group: isProject
          ? {
              key,
              kind: "project",
              label: item.dealName?.trim() || `Project #${item.dealId}`,
              dealId: item.dealId,
              items: [],
              cardLaneCount: 0,
              laneCount: 1,
              compactLaneCount: 1,
            }
          : {
              key,
              kind: "category",
              category: item.category,
              label:
                CATEGORY_ROWS.find((row) => row.category === item.category)
                  ?.label ?? "Other",
              dealId: null,
              items: [],
              cardLaneCount: 0,
              laneCount: 1,
              compactLaneCount: 1,
            },
        raw: [],
      };
      byGroup.set(key, entry);
    }
    entry.raw.push(item);
  }

  const groups = Array.from(byGroup.values()).map(({ group, raw }) => {
    const placedItems = raw
      .map((item) => placeTimelineItem(item, range, dayWidth)!)
      .sort(
        (left, right) =>
          left.dayIndex - right.dayIndex ||
          sortByDateTime(left.item, right.item),
      );
    // Cards get their own lanes on top; the rest pack below them.
    const cards = placedItems.filter((placed) => placed.card);
    const others = placedItems.filter((placed) => !placed.card);
    const cardLanes = packTimelineLanes(cards, (placed) => placed.barDays);
    const lanes = packTimelineLanes(others, (placed) => placed.span);
    // Compact rows hide outside titles: only the bar / marker takes room.
    const compactLanes = packTimelineLanes(others, (placed) => placed.barDays);
    group.items = [
      ...cards.map((placed, index) => ({
        ...placed,
        lane: cardLanes.lanes[index],
        compactLane: cardLanes.lanes[index],
      })),
      ...others.map((placed, index) => ({
        ...placed,
        lane: lanes.lanes[index],
        compactLane: compactLanes.lanes[index],
      })),
    ];
    group.cardLaneCount = cardLanes.count;
    group.compactLaneCount = compactLanes.count;
    group.laneCount = lanes.count;
    return group;
  });

  const categoryOrder = (group: TimelineGroup) =>
    CATEGORY_ROWS.findIndex((row) => row.category === group.category);

  return groups.sort((left, right) => {
    if (left.kind !== right.kind) return left.kind === "project" ? -1 : 1;
    if (left.kind === "project") return left.label.localeCompare(right.label);
    return categoryOrder(left) - categoryOrder(right);
  });
};
